/**
 * Changing and removing a vote, said where it happens (2026-10-24) — the same words under the
 * in-article poll, the panel's and the rail card's:
 * - voted, signed in: "Tap another answer to change your vote, or yours to remove it.";
 * - voted, signed out: nothing upfront; a re-tap raises the sign-in card ("You picked <X>. Sign in
 *   to change or remove your vote." + Sign in) for `POLL_SIGN_IN_ASK_MS` — the login dialog opens
 *   only from its button (Guillaume, 2026-10-25: the standing "Sign in to change your vote." line
 *   read as a nag under every vote);
 * - just removed: "Vote removed — <X> no longer shows on your comments. Undo" for `POLL_UNDO_MS`.
 * On the reader's own choice, signed in, "Your pick" reads "Remove" on hover or keyboard focus
 * (`MineMark` + `removableChoice`).
 */
import clsx from 'clsx'
import { FC } from 'react'
import type { PollVotedFrom } from 'shared-code/app-events'
import type { DSPoll, DSPostContext } from 'shared-definitions/types'
import { AuthContext } from '../contexts/AuthContext'
import CloseIcon from 'shared-svg/Close.svg'
import { PollStoreContext, SIGN_IN_TO_CHANGE_VOTE, usePollUndo } from './PollStoreContext'
import type { PollState } from './poll-store'
import styles from './PollFootnote.module.css'

export const CHANGE_OR_REMOVE_HINT =
  'Tap another answer to change your vote, or yours to remove it.'

/** The sign-in card's line: "You picked <X>." when the pick is known. */
export function signInAskLine(label: string | null): string {
  return label
    ? `You picked ${label}. Sign in to change or remove your vote.`
    : 'Sign in to change or remove your vote.'
}

/** "Vote removed — <X> no longer shows on your comments." */
export function removedLine(label: string | null): string {
  return label ? `Vote removed — ${label} no longer shows on your comments.` : 'Vote removed.'
}

/** Signed in with commentHub's poll routes: a tap on the own pick removes it. */
export function useCanRemoveVote(): boolean {
  const store = PollStoreContext.useOptionalContainer()
  const auth = AuthContext.useOptionalContainer()
  return Boolean(store?.hubEnabled && auth?.authState)
}

/** The class the reader's own choice button takes when a tap removes it (the pill swap). */
export function removableChoice(mine: boolean, canRemove: boolean): string | undefined {
  return mine && canRemove ? styles.removable : undefined
}

/** The accessible name of the reader's own choice when a tap removes it; else none (the text). */
export function removeVoteLabel(
  label: string,
  mine: boolean,
  canRemove: boolean
): string | undefined {
  return mine && canRemove ? `Remove your vote: ${label}` : undefined
}

/** " · Your pick", or " · Remove" while the own choice is hovered or focused (signed in). */
export const MineMark: FC<{ className?: string; canRemove: boolean; idle?: string }> = ({
  className,
  canRemove,
  idle = ' · Your pick',
}) => {
  if (!canRemove) {
    return <span className={className}>{idle}</span>
  }
  return (
    <span className={className}>
      <span className={styles.mineIdle}>{idle}</span>
      <span className={styles.mineRemove}>{' · Remove'}</span>
    </span>
  )
}

interface PollFootnoteProps {
  poll: DSPoll
  state: PollState
  from: PollVotedFrom
  postMeta: DSPostContext | null
  className?: string
  /** Draw the change/remove hint; false where no answer is on screen to tap (the rail's folded results). */
  hint?: boolean
}

const PollFootnote: FC<PollFootnoteProps> = ({
  poll,
  state,
  from,
  postMeta,
  className,
  hint = true,
}) => {
  const auth = AuthContext.useOptionalContainer()
  const store = PollStoreContext.useOptionalContainer()
  const canRemove = useCanRemoveVote()
  const undo = usePollUndo(poll, from, postMeta)

  if (state.signInAsk && auth && !auth.authState) {
    const label = state.choices.find(c => c.id === state.myChoiceId)?.label ?? null
    return (
      // Keyed by the re-tap: a second re-tap replays the entrance.
      <div
        key={state.signInAsk.seq}
        className={clsx(styles.ask, className)}
        role="status"
        aria-live="polite"
      >
        <p className={styles.askLine}>{signInAskLine(label)}</p>
        <button
          type="button"
          className={styles.askSignIn}
          onClick={() => {
            store?.dismissSignInAsk(poll.id)
            void auth.showLogIn({ copy: SIGN_IN_TO_CHANGE_VOTE })
          }}
        >
          {'Sign in'}
        </button>
        <button
          type="button"
          className={styles.askClose}
          aria-label="Close"
          onClick={() => store?.dismissSignInAsk(poll.id)}
        >
          <CloseIcon className={styles.askCloseIcon} aria-hidden="true" />
        </button>
      </div>
    )
  }

  if (state.undo) {
    const label = state.choices.find(c => c.id === state.undo?.choiceId)?.label ?? null
    return (
      <p className={clsx(styles.footnote, styles.undo, className)} role="status">
        {removedLine(label)}{' '}
        <button
          type="button"
          className={styles.action}
          onClick={() => {
            void undo()
          }}
        >
          {'Undo'}
        </button>
      </p>
    )
  }
  if (state.myChoiceId === null) {
    return null
  }
  if (canRemove && hint) {
    return <p className={clsx(styles.footnote, className)}>{CHANGE_OR_REMOVE_HINT}</p>
  }
  return null
}

export default PollFootnote
